import React, { Component } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import './BeatModerationPage.css';

const statuses = { PENDING: 'Chờ duyệt', APPROVED: 'Đã duyệt', REJECTED: 'Từ chối' };

// Mã blockReason của API detail và mã lỗi API decision dùng chung một bảng map (KF-29086 mục 6.2).
const blockReasons = {
    SINGER_REQUIRED: 'Beat chưa có ca sĩ. Gán ca sĩ trước khi duyệt.',
    SINGER_NOT_APPROVED: 'Còn ca sĩ chưa được duyệt. Bấm "Duyệt ca sĩ" ở danh sách bên dưới, hoặc đổi sang ca sĩ khác.',
    SINGER_INVALID: 'Ca sĩ bị ẩn, thiếu ảnh hoặc không hợp lệ. Chọn lại ca sĩ.',
    VOCAL_DETECTION_IN_PROGRESS: 'Chưa có kết quả AI kiểm tra giọng hát nên chưa duyệt được (vẫn từ chối được bình thường).'
};

const vocalLabels = { PENDING: 'Đang kiểm tra', PASSED: 'Không có giọng hát', REJECTED: 'Phát hiện giọng hát', ERROR: 'Kiểm tra lỗi' };

const singerStatuses = { ACTIVE: 'Đã duyệt', PENDING_REVIEW: 'Chờ duyệt', HIDDEN: 'Đã ẩn' };

const MAX_SINGERS = 5;
// Search ca sĩ của backend là scan phân trang (SingerAdminService.MAX_SEARCH_SCAN_PAGES): trang đầu có thể
// trả 0 match kèm nextCursor, phải đi tiếp mới ra kết quả. Giống vòng scan của SingerModerationPage.
const SINGER_SEARCH_MAX_SCAN = 20;
const SINGER_SEARCH_MAX_RESULTS = 50;
/** Số dòng lyric hiển thị quanh dòng đang hát — giống BeatPreviewStep của ikaraNext. */
const LYRIC_VISIBLE_RADIUS = 2;

/** `mm:ss:SSS` hoặc giây thập phân -> ms. Trả null khi sai cú pháp (cùng contract với ikaraNext). */
function parseTimeToMs(value) {
    if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? Math.round(value * 1000) : null;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const clock = /^(\d{1,});(\d{2});(\d{3})$/.exec(trimmed.replace(/:/g, ';'));
    if (clock) {
        const seconds = Number(clock[2]);
        if (seconds >= 60) return null;
        return Number(clock[1]) * 60000 + seconds * 1000 + Number(clock[3]);
    }
    if (/^\d+(\.\d+)?$/.test(trimmed)) return Math.round(Number(trimmed) * 1000);
    return null;
}

const GUIDE_STORAGE_KEY = 'beat-moderation-guide-open';

/** Guide mở sẵn lần đầu; admin thu lại thì nhớ. localStorage có thể bị chặn nên bọc try. */
function readGuideOpen() {
    try {
        return window.localStorage.getItem(GUIDE_STORAGE_KEY) !== '0';
    } catch (error) {
        return true;
    }
}

function writeGuideOpen(open) {
    try {
        window.localStorage.setItem(GUIDE_STORAGE_KEY, open ? '1' : '0');
    } catch (error) {
        // Storage bị chặn: guide vẫn dùng được, chỉ không nhớ trạng thái.
    }
}

/**
 * Hộp xác nhận dùng chung. Swal thay `window.confirm` vì Chrome cho user tick "Prevent this page from
 * creating additional dialogs" — từ đó mọi `confirm` trả false và thao tác im lặng không chạy, không
 * chấp nhận được với duyệt/gỡ beat. Tham số bám mẫu Swal sẵn có của repo (BotRecordingPage...).
 */
async function confirmAction(text) {
    const result = await Swal.fire({
        title: 'Xác nhận',
        text,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'OK',
        cancelButtonText: 'Hủy bỏ',
        allowOutsideClick: false
    });
    return result.isConfirmed;
}

function formatClock(ms) {
    if (ms === null || ms === undefined || !Number.isFinite(ms)) return '--:--';
    const safe = Math.max(0, Math.round(ms));
    return String(Math.floor(safe / 60000)).padStart(2, '0') + ':' + String(Math.floor((safe % 60000) / 1000)).padStart(2, '0');
}

/**
 * `Lyric.json` -> dòng có timing cho preview karaoke. Cùng cách đọc như `parseLyricContent` của
 * ikaraNext: mốc từ lấy `stringTime` rồi tới `startTime`; dữ liệu cũ bị trim() mất khoảng trắng nên
 * khi không từ nào có whitespace thì nối bằng ' ' cho chữ không dính.
 */
function parseLyricLines(content) {
    if (!content) return [];
    let parsed = content;
    if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch (error) { return []; }
    }
    const rawLines = parsed && (parsed.lines || parsed.sentences);
    if (!Array.isArray(rawLines)) return [];
    return rawLines.map((rawLine, index) => {
        if (!rawLine || typeof rawLine !== 'object') return null;
        const rawWords = Array.isArray(rawLine.words) ? rawLine.words : [];
        const words = rawWords
            .filter(word => word && typeof (word.text || word.word) === 'string')
            .map(word => ({ text: word.text || word.word, timeMs: parseTimeToMs(word.stringTime !== undefined ? word.stringTime : word.startTime) }));
        if (!words.length) {
            const text = String(rawLine.text || '').trim();
            if (!text) return null;
            return { id: 'l' + index, text, startMs: parseTimeToMs(rawLine.startTime), endMs: parseTimeToMs(rawLine.endTime), words: null };
        }
        const hasWhitespace = words.some(word => /\s/.test(word.text));
        const text = (words.length > 1 && !hasWhitespace ? words.map(w => w.text).join(' ') : words.map(w => w.text).join('')).trim();
        const startMs = parseTimeToMs(rawLine.startTime);
        return {
            id: 'l' + index, text,
            startMs: startMs !== null ? startMs : words[0].timeMs,
            endMs: parseTimeToMs(rawLine.endTime),
            words: words.length > 1 ? words : null
        };
    }).filter(line => line && line.text);
}

/** Khối preview karaoke: lời chạy giữa khung theo currentMs, dòng đang hát tô từng từ. */
function LyricPreview({ lines, currentMs }) {
    if (!lines.length) return <div className="beat-moderation-lyric-empty">Beat chưa có lyric đồng bộ để chạy theo nhạc.</div>;
    // Dòng đang hát = dòng cuối cùng có mốc <= thời điểm hiện tại.
    let activeIndex = -1;
    lines.forEach((line, index) => { if (line.startMs !== null && line.startMs <= currentMs) activeIndex = index; });
    const anchor = activeIndex === -1 ? 0 : activeIndex;
    return <div className="beat-moderation-karaoke">
        {lines.map((line, index) => {
            if (index < anchor - LYRIC_VISIBLE_RADIUS || index > anchor + LYRIC_VISIBLE_RADIUS + 1) return null;
            const isActive = index === activeIndex;
            if (!isActive || !line.words) {
                return <p key={line.id} className={'beat-moderation-karaoke-line' + (isActive ? ' is-active' : '')}>{line.text}</p>;
            }
            let activeWordIndex = -1;
            line.words.forEach((word, wordIndex) => { if (word.timeMs !== null && word.timeMs <= currentMs) activeWordIndex = wordIndex; });
            return <p key={line.id} className="beat-moderation-karaoke-line is-active">
                {line.words.map((word, wordIndex) => <span key={wordIndex} className={wordIndex <= activeWordIndex ? 'is-sung' : ''}>{word.text}</span>)}
            </p>;
        })}
    </div>;
}

export default class BeatModerationPage extends Component {
    state = {
        status: 'PENDING', items: [], cursor: null, loading: false, busy: false,
        detail: null, decision: null, reasonNote: '', vocalConfirmed: false,
        singerIds: null, singerQuery: '', singerResults: [], searchingSinger: false, singerSearched: false,
        lyricIndex: 0, currentMs: 0, playing: false, audioDurationMs: null, showRawLyric: false,
        revoking: false, revokeNote: '', guideOpen: readGuideOpen()
    };
    requestId = 0;
    detailId = 0;
    mounted = false;
    deciding = false;
    idempotencyKey = null;
    audio = React.createRef();

    componentDidMount() { this.mounted = true; this.load(true); }
    componentWillUnmount() { this.mounted = false; this.requestId++; this.detailId++; }

    endpoint = path => global.config.apiDomain + '/yt-song/web/beat-moderation' + path;
    data = response => {
        if (!response.data || response.data.status !== 'OK') throw new Error(response.data && response.data.message || 'Không thể xử lý yêu cầu');
        return response.data.data;
    };
    report = error => toast.error(error.response && error.response.data && error.response.data.message || error.message);

    load = async reset => {
        const id = ++this.requestId;
        const cursor = reset ? undefined : this.state.cursor;
        this.setState({ loading: true });
        try {
            const page = this.data(await axios.get(this.endpoint(''), { params: { status: this.state.status, limit: 50, cursor } }));
            if (!this.mounted || id !== this.requestId) return;
            this.setState(state => ({ items: reset ? page.items || [] : state.items.concat(page.items || []), cursor: page.nextCursor || null }));
        } catch (error) { if (this.mounted && id === this.requestId) this.report(error); }
        finally { if (this.mounted && id === this.requestId) this.setState({ loading: false }); }
    };

    changeStatus = status => {
        this.requestId++;
        this.setState({ status, items: [], cursor: null }, () => this.load(true));
    };

    open = async songId => {
        const id = ++this.detailId;
        this.setState({ busy: true });
        try {
            const detail = this.data(await axios.get(this.endpoint('/' + songId)));
            if (!this.mounted || id !== this.detailId) return;
            // idempotencyKey gắn với một lần mở beat: retry cùng quyết định trả lại kết quả cũ thay vì ghi hai lần.
            this.idempotencyKey = this.newIdempotencyKey();
            this.setState({
                detail, decision: null, reasonNote: '', vocalConfirmed: false, singerIds: null,
                singerQuery: '', singerResults: [], singerSearched: false,
                lyricIndex: 0, currentMs: 0, playing: false, audioDurationMs: detail.durationMs || null, showRawLyric: false,
                revoking: false, revokeNote: ''
            });
        } catch (error) { if (this.mounted && id === this.detailId) this.report(error); }
        finally { if (this.mounted && id === this.detailId) this.setState({ busy: false }); }
    };

    close = () => {
        if (this.state.busy) return;
        this.detailId++;
        this.setState({
            detail: null, decision: null, reasonNote: '', vocalConfirmed: false, singerIds: null,
            singerQuery: '', singerResults: [], singerSearched: false, playing: false, currentMs: 0,
            revoking: false, revokeNote: ''
        });
    };

    newIdempotencyKey = () => {
        const random = new Uint32Array(4);
        window.crypto.getRandomValues(random);
        return Array.from(random, value => value.toString(16).padStart(8, '0')).join('');
    };

    /** Danh sách ca sĩ sẽ gửi lên: null = giữ nguyên lựa chọn của user, mảng = CSKH đã sửa. */
    currentSingers = () => this.state.singerIds !== null
        ? this.state.singerIds
        : (this.state.detail.singers || []).map(singer => ({ id: singer.id, name: singer.name, avatarUrl: singer.avatarUrl, status: singer.status, approved: singer.approved }));

    /**
     * Search ca sĩ: backend lọc theo tên bằng cách scan phân trang nên trang đầu có thể rỗng mà vẫn còn
     * cursor — phải đi tiếp tới khi đủ kết quả hoặc hết dữ liệu, nếu không CSKH thấy "không tìm thấy"
     * dù ca sĩ có thật.
     */
    searchSinger = async event => {
        event.preventDefault();
        const query = this.state.singerQuery.trim();
        if (!query || this.state.searchingSinger) return;
        this.setState({ searchingSinger: true, singerResults: [], singerSearched: true });
        try {
            const found = [];
            const seen = new Set();
            let cursor;
            for (let scan = 0; scan < SINGER_SEARCH_MAX_SCAN; scan++) {
                const page = this.data(await axios.get(global.config.apiDomain + '/yt-song/web/singers/list',
                    { params: { status: 'ACTIVE', query, limit: 50, cursor } }));
                if (!this.mounted) return;
                // Lọc type=1 và có ảnh: đúng điều kiện isApprovable của server, tránh gán ca sĩ chắc chắn bị chặn.
                found.push(...(page.items || []).filter(singer => singer.type === 1 && singer.avatarUrl));
                this.setState({ singerResults: found.slice(0, SINGER_SEARCH_MAX_RESULTS) });
                cursor = page.nextCursor && page.nextCursor !== 'END' ? page.nextCursor : null;
                if (!cursor || found.length >= SINGER_SEARCH_MAX_RESULTS) break;
                if (seen.has(cursor)) throw new Error('Cursor tìm kiếm bị lặp');
                seen.add(cursor);
            }
        } catch (error) { if (this.mounted) this.report(error); }
        finally { if (this.mounted) this.setState({ searchingSinger: false }); }
    };

    addSinger = singer => {
        const singers = this.currentSingers();
        if (singers.some(item => item.id === singer.id)) { toast.warning('Ca sĩ này đã có trong beat'); return; }
        if (singers.length >= MAX_SINGERS) { toast.warning('Mỗi beat chỉ nhận tối đa ' + MAX_SINGERS + ' ca sĩ'); return; }
        this.setState({ singerIds: singers.concat(this.toEditorSinger(singer)) });
    };

    /** Thay toàn bộ ca sĩ bằng một ca sĩ mới — thao tác chính khi ca sĩ user chọn không phù hợp. */
    replaceSingers = singer => {
        this.setState({ singerIds: [this.toEditorSinger(singer)] });
        toast.info('Đã thay toàn bộ ca sĩ bằng "' + singer.name + '". Bấm Duyệt beat để lưu.');
    };

    // Kết quả search đã lọc ACTIVE + type=1 + có ảnh nên chắc chắn đủ điều kiện duyệt.
    toEditorSinger = singer => ({ id: singer.id, name: singer.name, avatarUrl: singer.avatarUrl, status: singer.status, approved: true });

    /**
     * Duyệt ca sĩ ngay trên màn kiểm duyệt beat (chốt 08/10/2026) — trước đó CSKH phải sang trang
     * Quản lý ca sĩ rồi quay lại, mất mạch làm việc. Dùng chính API của KF-30098.
     *
     * <p>Duyệt ca sĩ xong phải RELOAD detail: `approvable`/`blockReason`/`pendingSingerCount` do
     * server tính, client không tự suy lại được. Lưu ý `approveToActive` chỉ set ACTIVE mà KHÔNG
     * kiểm tra ảnh, nên ca sĩ thiếu ảnh duyệt xong beat vẫn bị chặn — cảnh báo trước khi gửi.
     */
    approveSinger = async (singer) => {
        if (this.state.busy || !this.state.detail) return;
        if (!singer.avatarUrl) {
            toast.warning('Ca sĩ "' + singer.name + '" chưa có ảnh đại diện. Vào trang Quản lý ca sĩ thêm ảnh trước, nếu không beat vẫn không duyệt được.');
            return;
        }
        if (!await confirmAction('Duyệt ca sĩ "' + singer.name + '"? Ca sĩ sẽ hiển thị công khai trên toàn hệ thống.')) return;
        const songId = this.state.detail.songId;
        this.setState({ busy: true });
        try {
            this.data(await axios.post(global.config.apiDomain + '/yt-song/web/singers/approve',
                { singerId: singer.id }));
            if (!this.mounted) return;
            toast.success('Đã duyệt ca sĩ "' + singer.name + '"');
            // CSKH đã sửa danh sách thì currentSingers() đọc từ singerIds, không đọc detail — phải
            // cập nhật cả hai, nếu không ca sĩ vừa duyệt vẫn hiện "Chờ duyệt" và nút Duyệt beat vẫn bị chặn.
            if (this.state.singerIds !== null) {
                this.setState(state => ({
                    singerIds: state.singerIds.map(item =>
                        item.id === singer.id ? { ...item, status: 'ACTIVE', approved: true } : item)
                }));
            }
            // Beat đang mở có thể đã đủ điều kiện duyệt — lấy lại trạng thái từ server.
            await this.reloadDetail(songId);
        } catch (error) { if (this.mounted) this.report(error); }
        finally { if (this.mounted) this.setState({ busy: false }); }
    };

    /** Nạp lại chi tiết beat đang mở, giữ nguyên mọi sửa ca sĩ CSKH chưa gửi. */
    reloadDetail = async (songId) => {
        const id = ++this.detailId;
        try {
            const detail = this.data(await axios.get(this.endpoint('/' + songId)));
            if (!this.mounted || id !== this.detailId) return;
            this.setState({ detail });
        } catch (error) { if (this.mounted && id === this.detailId) this.report(error); }
    };

    removeSinger = singerId => this.setState({ singerIds: this.currentSingers().filter(item => item.id !== singerId) });

    resetSingers = () => this.setState({ singerIds: null });

    decide = async () => {
        const { detail, decision, reasonNote, vocalConfirmed, singerIds } = this.state;
        if (this.deciding || this.state.busy || !detail || !decision) return;
        const reject = decision === 'REJECTED';
        if (reject && !reasonNote.trim()) { toast.warning('Cần ghi lý do khi từ chối beat'); return; }
        if (!reject) {
            const singers = this.currentSingers();
            if (!singers.length) { toast.warning('Beat chưa có ca sĩ, cần gán ca sĩ trước khi duyệt'); return; }
            if (singers.some(singer => !singer.approved)) { toast.warning('Còn ca sĩ chưa đủ điều kiện. Duyệt ca sĩ hoặc thay bằng ca sĩ khác.'); return; }
            if (this.vocalRejected() && !vocalConfirmed) { toast.warning('Cần tích xác nhận override cảnh báo giọng hát'); return; }
        }
        const beatLabel = detail.songName || detail.songId;
        if (!await confirmAction(reject
            ? 'Từ chối beat "' + beatLabel + '"? Beat sẽ không được phát hành.'
            : 'Duyệt và phát hành beat "' + beatLabel + '"?')) return;
        const request = { decision, expectedVersion: detail.moderationVersion, idempotencyKey: this.idempotencyKey };
        if (reject) {
            request.reasonNote = reasonNote.trim();
        } else {
            if (singerIds !== null) request.singerIds = singerIds.map(singer => singer.id);
            if (this.vocalRejected()) request.vocalOverrideConfirmed = true;
        }
        this.deciding = true;
        this.setState({ busy: true });
        try {
            const result = this.data(await axios.post(this.endpoint('/' + detail.songId + '/decision'), request));
            if (!this.mounted) return;
            // duplicate = beat đã được quyết định trước đó (retry hoặc CSKH khác thao tác trùng).
            if (result.duplicate) {
                toast.info('Beat đã được xử lý trước đó, trạng thái hiện tại: ' + (statuses[result.moderationStatus] || result.moderationStatus));
            } else {
                toast.success(reject ? 'Đã từ chối beat' : 'Đã duyệt beat');
            }
            this.detailId++;
            this.setState({
                detail: null, decision: null, reasonNote: '', vocalConfirmed: false, singerIds: null,
                singerResults: [], singerQuery: '', singerSearched: false, playing: false, items: [], cursor: null
            }, () => this.load(true));
        } catch (error) { if (this.mounted) this.report(error); }
        finally {
            this.deciding = false;
            if (this.mounted) this.setState({ busy: false });
        }
    };

    /**
     * Thu hồi quyết định: beat về PENDING rồi quyết định lại bằng luồng duyệt/từ chối thường.
     * Áp cho cả beat APPROVED (gỡ khỏi public) và REJECTED (mở lại beat bị loại nhầm).
     */
    revoke = async () => {
        const { detail, revokeNote } = this.state;
        if (this.deciding || this.state.busy || !detail) return;
        if (!revokeNote.trim()) { toast.warning('Cần ghi lý do khi mở lại beat'); return; }
        const approved = detail.moderationStatus === 'APPROVED';
        if (!await confirmAction(approved
            ? 'Mở lại beat "' + (detail.songName || detail.songId) + '"? Beat sẽ bị gỡ khỏi tìm kiếm và về hàng chờ duyệt.'
            : 'Mở lại beat "' + (detail.songName || detail.songId) + '" để duyệt lại?')) return;
        this.deciding = true;
        this.setState({ busy: true });
        try {
            const result = this.data(await axios.post(this.endpoint('/' + detail.songId + '/revoke'),
                { expectedVersion: detail.moderationVersion, idempotencyKey: this.idempotencyKey, reasonNote: revokeNote.trim() }));
            if (!this.mounted) return;
            if (result.duplicate) {
                toast.info('Beat đã được xử lý trước đó, trạng thái hiện tại: ' + (statuses[result.moderationStatus] || result.moderationStatus));
            } else {
                toast.success('Đã mở lại beat, beat về hàng chờ duyệt');
            }
            this.detailId++;
            this.setState({
                detail: null, decision: null, reasonNote: '', vocalConfirmed: false, singerIds: null,
                singerResults: [], singerQuery: '', singerSearched: false, playing: false,
                revoking: false, revokeNote: '', items: [], cursor: null
            }, () => this.load(true));
        } catch (error) { if (this.mounted) this.report(error); }
        finally {
            this.deciding = false;
            if (this.mounted) this.setState({ busy: false });
        }
    };

    vocalRejected = () => this.state.detail && this.state.detail.vocal && this.state.detail.vocal.status === 'REJECTED';
    vocalPending = () => !!(this.state.detail && this.state.detail.vocalDetectionInProgress);

    togglePlay = () => {
        const audio = this.audio.current;
        if (!audio) return;
        if (audio.paused) {
            const played = audio.play();
            if (played && played.catch) played.catch(error => toast.error('Không thể phát beat: ' + error.message));
        } else {
            audio.pause();
        }
    };

    seek = event => {
        const ms = Number(event.target.value);
        const audio = this.audio.current;
        // Gán currentTime khi media chưa nạp xong sẽ ném InvalidStateError.
        if (audio && audio.readyState > 0) {
            try { audio.currentTime = ms / 1000; } catch (error) { /* chưa seek được, bỏ qua */ }
        }
        this.setState({ currentMs: ms });
    };

    toggleGuide = () => this.setState(state => {
        writeGuideOpen(!state.guideOpen);
        return { guideOpen: !state.guideOpen };
    });

    /** Guide ngắn cho CSKH: chỉ những luật mà bấm sai mới biết, viết dạng mũi tên cho đọc nhanh. */
    renderGuide() {
        return <section className="beat-moderation-guide">
            <ol>
                <li><b>Nghe beat</b> &rarr; lời phải chạy khớp nhạc. Lời lệch, thiếu câu, hoặc beat có giọng hát &rArr; <b>Từ chối</b>.</li>
                <li><b>Ca sĩ</b> &rarr; phải <b>đã duyệt + có ảnh</b>. Ca sĩ chờ duyệt &rArr; bấm <b>Duyệt ca sĩ</b> ngay tại đây. Ca sĩ sai &rArr; <b>Thay tất cả</b> (đổi hẳn) hoặc <b>Thêm</b> (hát cùng, tối đa {MAX_SINGERS}).</li>
                <li><b>AI giọng hát</b> &rarr; chưa có kết quả thì <b>chưa duyệt được</b> (từ chối thì được). AI báo <i>Phát hiện giọng hát</i> &rArr; vẫn duyệt được nhưng phải tích xác nhận &mdash; <b>quyết định của bạn thắng AI</b>.</li>
                <li><b>Duyệt</b> &rarr; beat lên public ngay. <b>Từ chối</b> &rarr; bắt buộc ghi lý do (nội bộ, user không thấy).</li>
                <li><b>Bấm nhầm?</b> &rarr; mở beat đó &rarr; <b>Mở lại để duyệt</b> &rArr; beat về hàng chờ, gỡ khỏi tìm kiếm.</li>
            </ol>
            <p className="beat-moderation-muted">Nút <b>Duyệt beat</b> bị mờ &rArr; đọc băng vàng phía trên, nó nói rõ còn thiếu gì.</p>
        </section>;
    }

    time = value => value ? new Date(value).toLocaleString('vi-VN') : '—';
    percent = ratio => typeof ratio === 'number' ? Math.round(ratio * 100) + '%' : '—';

    renderOwner(owner) {
        if (!owner) return <p className="beat-moderation-muted">Không đọc được thông tin người đăng.</p>;
        return <div className="beat-moderation-owner">
            {owner.avatarUrl
                ? <img src={owner.avatarUrl} alt={owner.name || 'Người đăng'} onError={event => { event.target.style.visibility = 'hidden'; }} />
                : <span className="beat-moderation-noavatar">?</span>}
            <div>
                <strong>{owner.name || 'Chưa có tên'}</strong>
                <small>uid {owner.uid !== null && owner.uid !== undefined ? owner.uid : '—'}</small>
                <small>userId {owner.id || '—'}</small>
            </div>
        </div>;
    }

    renderSingerEditor() {
        const { busy, singerIds, singerQuery, singerResults, searchingSinger, singerSearched } = this.state;
        const singers = this.currentSingers();
        return <div className="beat-moderation-field">
            <label>Ca sĩ của beat ({singers.length}/{MAX_SINGERS}){singerIds !== null && <button className="beat-moderation-link" disabled={busy} onClick={this.resetSingers}>Hoàn tác thay đổi</button>}</label>
            {!singers.length
                ? <p className="beat-moderation-warn">Beat chưa có ca sĩ — tìm và thêm ca sĩ bên dưới trước khi duyệt.</p>
                : <ul className="beat-moderation-singers">{singers.map(singer => <li key={singer.id} className={singer.approved ? '' : 'is-pending'}>
                    {singer.avatarUrl
                        ? <img src={singer.avatarUrl} alt={singer.name} onError={event => { event.target.style.visibility = 'hidden'; }} />
                        : <span className="beat-moderation-noavatar">?</span>}
                    <div><strong>{singer.name}</strong><small>ID {singer.id} · {singer.approved ? 'Đủ điều kiện' : (singer.status === 'PENDING_REVIEW' ? 'Chờ duyệt ca sĩ' : 'Không hợp lệ / thiếu ảnh')}</small></div>
                    {singer.status === 'PENDING_REVIEW' && <button className="beat-moderation-button beat-moderation-approve-singer" disabled={busy} onClick={() => this.approveSinger(singer)}>Duyệt ca sĩ</button>}
                    <button className="beat-moderation-link beat-moderation-remove" disabled={busy} onClick={() => this.removeSinger(singer.id)}>Bỏ</button>
                </li>)}</ul>}
            <form className="beat-moderation-singer-search" onSubmit={this.searchSinger}>
                <input aria-label="Tìm ca sĩ đã duyệt" placeholder="Tìm ca sĩ đã duyệt theo tên (có dấu hoặc không dấu)..." maxLength={100} value={singerQuery} disabled={busy} onChange={event => this.setState({ singerQuery: event.target.value })} />
                <button type="submit" className="beat-moderation-button" disabled={busy || searchingSinger || !singerQuery.trim()}>{searchingSinger ? 'Đang tìm...' : 'Tìm ca sĩ'}</button>
            </form>
            {searchingSinger && <p className="beat-moderation-muted" role="status">Đang quét danh sách ca sĩ đã duyệt...</p>}
            {!searchingSinger && singerSearched && !singerResults.length && <p className="beat-moderation-warn">Không tìm thấy ca sĩ <strong>đã duyệt</strong> nào khớp tên. Thử từ khoá ngắn hơn — ca sĩ đang chờ duyệt thì bấm <strong>Duyệt ca sĩ</strong> ngay ở danh sách trên.</p>}
            {!!singerResults.length && <>
                <p className="beat-moderation-muted">{singerResults.length} ca sĩ khớp — <strong>Thay tất cả</strong> để đổi hẳn sang ca sĩ này, <strong>Thêm</strong> để hát cùng ca sĩ hiện có.</p>
                <ul className="beat-moderation-singer-results">{singerResults.map(singer => <li key={singer.id}>
                    {singer.avatarUrl
                        ? <img src={singer.avatarUrl} alt={singer.name} onError={event => { event.target.style.visibility = 'hidden'; }} />
                        : <span className="beat-moderation-noavatar">?</span>}
                    <div><strong>{singer.name}</strong><small>ID {singer.id} · {singer.songCount || 0} bài</small></div>
                    <button className="beat-moderation-button beat-moderation-replace" disabled={busy} onClick={() => this.replaceSingers(singer)}>Thay tất cả</button>
                    <button className="beat-moderation-button" disabled={busy} onClick={() => this.addSinger(singer)}>Thêm</button>
                </li>)}</ul>
            </>}
        </div>;
    }

    renderPreview(detail, lines, durationMs) {
        const { lyricIndex, currentMs, playing, showRawLyric } = this.state;
        const lyrics = detail.lyrics || [];
        const syncedCount = lines.filter(line => line.startMs !== null).length;
        return <section className="beat-moderation-preview">
            <audio
                ref={this.audio}
                {...(detail.songUrl ? { src: detail.songUrl } : {})}
                preload="metadata"
                onPlay={() => this.setState({ playing: true })}
                onPause={() => this.setState({ playing: false })}
                onTimeUpdate={event => this.setState({ currentMs: Math.round(event.target.currentTime * 1000) })}
                onLoadedMetadata={event => { if (Number.isFinite(event.target.duration)) this.setState({ audioDurationMs: Math.round(event.target.duration * 1000) }); }}
                onError={() => this.setState({ playing: false })}
            />
            {!detail.songUrl && <div className="beat-moderation-alert">Không có link audio hợp lệ để nghe thử.</div>}
            <LyricPreview lines={lines} currentMs={currentMs} />
            <div className="beat-moderation-player">
                <button className="beat-moderation-play" disabled={!detail.songUrl} onClick={this.togglePlay} aria-label={playing ? 'Tạm dừng' : 'Phát beat'}>
                    <i className={'fa-solid ' + (playing ? 'fa-pause' : 'fa-play')} aria-hidden="true"></i>
                </button>
                <span className="beat-moderation-clock">{formatClock(currentMs)}</span>
                <input type="range" className="beat-moderation-seek" min={0} max={durationMs || 0} step={100}
                    value={Math.min(currentMs, durationMs || 0)} disabled={!detail.songUrl || !durationMs}
                    onChange={this.seek} aria-label="Tua beat" />
                <span className="beat-moderation-clock">{formatClock(durationMs)}</span>
            </div>
            <div className="beat-moderation-lyric-bar">
                {lyrics.length > 1 && <select className="form-control" value={lyricIndex} aria-label="Chọn bản lyric"
                    onChange={event => this.setState({ lyricIndex: Number(event.target.value), showRawLyric: false })}>
                    {lyrics.map((lyric, index) => <option key={lyric.lyricId || index} value={index}>Lyric {lyric.lyricId || index + 1}</option>)}
                </select>}
                <span className="beat-moderation-muted">{lines.length} câu · {syncedCount} câu có mốc thời gian{lines.length && syncedCount < lines.length ? ' — lời chưa đồng bộ hết' : ''}</span>
                {!!lines.length && <button className="beat-moderation-link" onClick={() => this.setState({ showRawLyric: !showRawLyric })}>{showRawLyric ? 'Ẩn toàn bộ lời' : 'Xem toàn bộ lời'}</button>}
            </div>
            {showRawLyric && <ol className="beat-moderation-lyric-all">{lines.map(line =>
                <li key={line.id}><span className="beat-moderation-lyric-time">{formatClock(line.startMs)}</span>{line.text}</li>)}</ol>}
        </section>;
    }

    renderDetail() {
        const { detail, busy, decision, reasonNote, vocalConfirmed, singerIds, lyricIndex, audioDurationMs, revoking, revokeNote } = this.state;
        const pending = detail.moderationStatus === 'PENDING';
        const edited = singerIds !== null;
        // Server là nguồn luật duy nhất (approvable/blockReason). Khi CSKH đổi ca sĩ, blockReason cũ nói về
        // ca sĩ đã bị thay nên không còn đúng — chỉ còn gate vocal là ràng buộc độc lập với ca sĩ.
        const singers = this.currentSingers();
        const approvable = edited
            ? singers.length > 0 && singers.every(singer => singer.approved) && !this.vocalPending()
            : detail.approvable;
        const activeLyric = (detail.lyrics || [])[lyricIndex];
        const lines = activeLyric ? parseLyricLines(activeLyric.content) : [];
        const durationMs = audioDurationMs || detail.durationMs || null;

        return <div className="modal d-block beat-moderation-modal" role="dialog" aria-modal="true" aria-labelledby="beat-moderation-title" style={{ background: 'rgba(0,0,0,.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-xl"><div className="modal-content">
                <div className="modal-header">
                    <h5 id="beat-moderation-title">{detail.songName || 'Beat #' + detail.songId}</h5>
                    <span className={'beat-moderation-badge beat-moderation-badge-' + detail.moderationStatus}>{statuses[detail.moderationStatus] || detail.moderationStatus}</span>
                </div>
                <div className="modal-body">
                    {detail.pendingSingerCount > 0 && !edited && <div className="beat-moderation-alert beat-moderation-alert-warn">Beat có <strong>{detail.pendingSingerCount}</strong> ca sĩ cần duyệt trước khi duyệt beat.</div>}
                    {!approvable && !edited && detail.blockReason && <div className="beat-moderation-alert beat-moderation-alert-warn">{blockReasons[detail.blockReason] || detail.blockReason}</div>}
                    {this.vocalPending() && (edited || detail.blockReason !== 'VOCAL_DETECTION_IN_PROGRESS') && <div className="beat-moderation-alert beat-moderation-alert-warn">{blockReasons.VOCAL_DETECTION_IN_PROGRESS}</div>}

                    {this.renderPreview(detail, lines, durationMs)}

                    <div className="beat-moderation-columns">
                        <div className="beat-moderation-field">
                            <label>Thông tin beat</label>
                            <dl className="beat-moderation-info">
                                <dt>Tên beat</dt><dd>{detail.songName || '—'}</dd>
                                <dt>Song ID</dt><dd>{detail.songId}</dd>
                                <dt>Tone</dt><dd>{detail.tone || '—'}</dd>
                                <dt>Thời lượng</dt><dd>{formatClock(durationMs)}</dd>
                                <dt>Gửi duyệt lúc</dt><dd>{this.time(detail.submittedAt)}</dd>
                                <dt>Phiên bản duyệt</dt><dd>{detail.moderationVersion}</dd>
                                <dt>File beat</dt><dd>{detail.songUrl
                                    ? <a href={detail.songUrl} target="_blank" rel="noopener noreferrer">Mở file audio</a>
                                    : 'Không có link hợp lệ'}</dd>
                                <dt>Ảnh bìa</dt><dd>{detail.thumbnailUrl
                                    ? <a href={detail.thumbnailUrl} target="_blank" rel="noopener noreferrer"><img className="beat-moderation-thumb" src={detail.thumbnailUrl} alt="Ảnh bìa beat" onError={event => { event.target.style.display = 'none'; }} /></a>
                                    : 'Chưa có ảnh'}</dd>
                            </dl>
                        </div>
                        <div className="beat-moderation-field">
                            <label>Người đăng</label>
                            {this.renderOwner(detail.owner)}
                            <label className="beat-moderation-sublabel">AI kiểm tra giọng hát</label>
                            {detail.vocal
                                ? <dl className="beat-moderation-info">
                                    <dt>Kết luận</dt><dd className={detail.vocal.status === 'REJECTED' ? 'beat-moderation-warn' : ''}>{vocalLabels[detail.vocal.status] || detail.vocal.status}</dd>
                                    <dt>Tỉ lệ giọng hát</dt><dd>{this.percent(detail.vocal.ratio)}</dd>
                                    <dt>Thời điểm</dt><dd>{this.time(detail.vocal.detectedAt)}</dd>
                                    <dt>Phiên bản AI</dt><dd>{detail.vocal.detectorVersion || '—'}</dd>
                                </dl>
                                : <p className="beat-moderation-muted">{this.vocalPending() ? 'Chưa có kết quả AI — beat chưa chạy hoặc đang chạy detect. Chưa duyệt được, vẫn từ chối được.' : 'Beat chưa có kết quả kiểm tra giọng hát.'}</p>}
                        </div>
                    </div>

                    {pending ? this.renderSingerEditor() : <div className="beat-moderation-field"><label>Ca sĩ của beat</label>
                        {(detail.singers || []).length
                            ? <ul className="beat-moderation-singers">{(detail.singers || []).map(singer => <li key={singer.id} className={singer.approved ? '' : 'is-pending'}>
                                {singer.avatarUrl
                                    ? <img src={singer.avatarUrl} alt={singer.name} onError={event => { event.target.style.visibility = 'hidden'; }} />
                                    : <span className="beat-moderation-noavatar">?</span>}
                                <div><strong>{singer.name}</strong><small>ID {singer.id} · {singerStatuses[singer.status] || singer.status}</small></div>
                            </li>)}</ul>
                            : <p className="beat-moderation-warn">Beat không có ca sĩ.</p>}
                    </div>}

                    {pending && <div className="beat-moderation-field"><label>Quyết định</label>
                        <div className="beat-moderation-decisions">
                            <button className={'beat-moderation-choice' + (decision === 'APPROVED' ? ' is-active' : '')} disabled={busy} onClick={() => this.setState({ decision: 'APPROVED' })}>Duyệt beat</button>
                            <button className={'beat-moderation-choice beat-moderation-choice-reject' + (decision === 'REJECTED' ? ' is-active' : '')} disabled={busy} onClick={() => this.setState({ decision: 'REJECTED' })}>Từ chối</button>
                        </div>
                    </div>}

                    {pending && decision === 'REJECTED' && <div className="beat-moderation-field">
                        <label htmlFor="beat-moderation-reason">Lý do từ chối (nội bộ, bắt buộc) {reasonNote.length}/1000</label>
                        <textarea id="beat-moderation-reason" className="form-control" rows={3} maxLength={1000} disabled={busy} value={reasonNote} onChange={event => this.setState({ reasonNote: event.target.value })} />
                        <small>Lý do chỉ dùng nội bộ CSKH, không gửi cho người đăng beat.</small>
                    </div>}

                    {pending && decision === 'APPROVED' && this.vocalRejected() && <div className="beat-moderation-alert beat-moderation-alert-danger">
                        <label className="beat-moderation-check"><input type="checkbox" disabled={busy} checked={vocalConfirmed} onChange={event => this.setState({ vocalConfirmed: event.target.checked })} />
                            Tôi đã nghe và xác nhận duyệt beat dù AI phát hiện giọng hát chiếm {this.percent(detail.vocal.ratio)} thời lượng.</label>
                    </div>}

                    {detail.moderationStatus === 'REJECTED' && <p className="beat-moderation-muted">Beat đã bị từ chối. Người đăng có thể sửa và gửi lại.</p>}

                    {!pending && <div className="beat-moderation-field"><label>Thu hồi quyết định</label>
                        {!revoking
                            ? <>
                                <p className="beat-moderation-muted">{detail.moderationStatus === 'APPROVED'
                                    ? 'Duyệt nhầm? Mở lại sẽ gỡ beat khỏi tìm kiếm và đưa về hàng chờ để quyết định lại.'
                                    : 'Từ chối nhầm? Mở lại để đưa beat về hàng chờ và duyệt lại.'}</p>
                                <button className="beat-moderation-button beat-moderation-revoke" disabled={busy} onClick={() => this.setState({ revoking: true })}>Mở lại để duyệt</button>
                            </>
                            : <>
                                <label htmlFor="beat-moderation-revoke-note">Lý do mở lại (nội bộ, bắt buộc) {revokeNote.length}/1000</label>
                                <textarea id="beat-moderation-revoke-note" className="form-control" rows={2} maxLength={1000} disabled={busy} value={revokeNote} onChange={event => this.setState({ revokeNote: event.target.value })} />
                                <div className="beat-moderation-decisions beat-moderation-revoke-actions">
                                    <button className="btn btn-warning" disabled={busy} onClick={this.revoke}>{busy ? 'Đang xử lý...' : 'Xác nhận mở lại'}</button>
                                    <button className="beat-moderation-button" disabled={busy} onClick={() => this.setState({ revoking: false, revokeNote: '' })}>Hủy</button>
                                </div>
                            </>}
                    </div>}
                </div>
                <div className="modal-footer">
                    {pending && decision && <button className={'btn ' + (decision === 'REJECTED' ? 'btn-danger' : 'btn-success')} disabled={busy || (decision === 'APPROVED' && !approvable)} onClick={this.decide}>
                        {busy ? 'Đang xử lý...' : (decision === 'REJECTED' ? 'Xác nhận từ chối' : 'Xác nhận duyệt')}
                    </button>}
                    <button className="btn btn-secondary" disabled={busy} onClick={this.close}>Đóng</button>
                </div>
            </div></div>
        </div>;
    }

    render() {
        const { status, items, cursor, loading, busy, detail, guideOpen } = this.state;
        return <div id="content" className="beat-moderation-page"><div className="container-fluid beat-moderation-inner">
            <div className="beat-moderation-heading">
                <div><span className="beat-moderation-eyebrow">QUẢN LÝ BEAT IKARA</span><h1>Kiểm duyệt beat người dùng upload</h1><p>Nghe beat với lời chạy khớp nhạc, kiểm tra ca sĩ trước khi phát hành.</p></div>
                <button className="beat-moderation-button" aria-expanded={guideOpen} onClick={this.toggleGuide}>
                    <i className="fa-solid fa-circle-question" aria-hidden="true"></i> {guideOpen ? 'Ẩn hướng dẫn' : 'Hướng dẫn'}
                </button>
            </div>
            {guideOpen && this.renderGuide()}
            <section className="beat-moderation-panel">
                <div className="beat-moderation-toolbar">
                    <div className="beat-moderation-tabs" role="group" aria-label="Lọc theo trạng thái">{Object.keys(statuses).map(key =>
                        <button key={key} className={'beat-moderation-tab' + (key === status ? ' is-active' : '')} aria-pressed={key === status} disabled={busy} onClick={() => this.changeStatus(key)}>{statuses[key]}</button>)}
                    </div>
                    <button className="beat-moderation-button" disabled={loading || busy} onClick={() => this.changeStatus(status)}><i className="fa-solid fa-rotate-right" aria-hidden="true"></i> Làm mới</button>
                </div>
                <div className="table-responsive beat-moderation-table"><table className="table table-hover">
                    <thead><tr><th>STT</th><th>Ảnh</th><th>Tên beat</th><th>Ca sĩ</th><th>UID</th><th>Gửi lúc</th><th>AI giọng hát</th><th>Trạng thái</th><th>Thao tác</th></tr></thead>
                    <tbody>{items.map((item, index) => <tr key={item.songId}>
                        <td>{index + 1}</td>
                        <td>{item.thumbnailUrl ? <img src={item.thumbnailUrl} alt={item.songName} onError={event => { event.target.style.visibility = 'hidden'; }} /> : 'Chưa có ảnh'}</td>
                        <td className="beat-moderation-name">{item.songName || 'Beat #' + item.songId}<small>ID {item.songId}</small></td>
                        <td>{item.singer || <span className="beat-moderation-warn">Chưa có ca sĩ</span>}</td>
                        <td>{item.ownerUid !== null && item.ownerUid !== undefined ? item.ownerUid : '—'}</td>
                        <td>{this.time(item.submittedAt)}</td>
                        <td>{item.vocalDetectionStatus
                            ? <span className={'beat-moderation-badge beat-moderation-vocal-' + item.vocalDetectionStatus}>{vocalLabels[item.vocalDetectionStatus] || item.vocalDetectionStatus}{typeof item.vocalRatio === 'number' && ' · ' + this.percent(item.vocalRatio)}</span>
                            : '—'}</td>
                        <td><span className={'beat-moderation-badge beat-moderation-badge-' + item.moderationStatus}>{statuses[item.moderationStatus] || item.moderationStatus}</span></td>
                        <td><button className="beat-moderation-button beat-moderation-detail-button" disabled={busy || loading} onClick={() => this.open(item.songId)}>{item.moderationStatus === 'PENDING' ? 'Kiểm duyệt' : 'Xem chi tiết'}</button></td>
                    </tr>)}</tbody>
                </table></div>
                {loading && <div className="beat-moderation-empty" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span><p>Đang tải danh sách beat...</p></div>}
                {!loading && !items.length && <div className="beat-moderation-empty"><i className="fa-solid fa-music" aria-hidden="true"></i><h3>Chưa có beat {statuses[status].toLowerCase()}</h3><p>Danh sách sẽ hiển thị tại đây khi có dữ liệu.</p></div>}
                <div className="beat-moderation-footer"><span>Đã tải {items.length} beat · {statuses[status]}</span>{cursor && <button className="beat-moderation-button" disabled={loading || busy} onClick={() => this.load(false)}>Xem thêm</button>}</div>
            </section>
            {detail && this.renderDetail()}
        </div></div>;
    }
}
